import axios from 'axios'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('propchain_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('propchain_token')
      localStorage.removeItem('propchain_user')
      window.location.href = '/'
    }
    return Promise.reject(error)
  }
)

// Tenant
export const tenantAPI = {
  getConfig: () => api.get('/tenant/config'),
}

// Tenant Admin (owner)
export const tenantAdminAPI = {
  getConfig: () => api.get('/tenant-admin/config'),
  updateBranding: (data) => api.put('/tenant-admin/branding', data),
  updateFeatures: (data) => api.put('/tenant-admin/features', data),
  getStats: () => api.get('/tenant-admin/stats'),
  listUsers: (params) => api.get('/tenant-admin/users', { params }),
}

// Exchange Rates
export const exchangeRatesAPI = {
  getRates: () => api.get('/exchange-rates'),
}

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getWalletChallenge: (address, chainId) => api.post('/auth/wallet/challenge', { address, chain_id: chainId }),
  verifyWallet: (address, signature, message) => api.post('/auth/wallet/verify', { address, signature, message }),
  connectWallet: (address, signature, message) => api.post('/auth/connect-wallet', { address, signature, message }),
  getMe: () => api.get('/auth/me'),
}

// KYC
export const kycAPI = {
  submit: (data) => api.post('/kyc/submit', data),
  getStatus: () => api.get('/kyc/status'),
  getPending: () => api.get('/admin/kyc/pending'),
  approve: (id) => api.post(`/admin/kyc/${id}/approve`),
  reject: (id, notes) => api.post(`/admin/kyc/${id}/reject`, { notes }),
}

// Properties
export const propertiesAPI = {
  list: (params) => api.get('/properties', { params }),
  get: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  submit: (id) => api.post(`/properties/${id}/submit`),
  approve: (id) => api.post(`/admin/properties/${id}/approve`),
  tokenize: (id) => api.post(`/admin/properties/${id}/tokenize`),
  getIssuerProperties: () => api.get('/issuer/properties'),
}

// Offerings
export const offeringsAPI = {
  list: (status) => api.get('/offerings', { params: { status } }),
  get: (id) => api.get(`/offerings/${id}`),
  create: (data) => api.post('/offerings', data),
  activate: (id) => api.post(`/offerings/${id}/activate`),
}

// Subscriptions
export const subscriptionsAPI = {
  create: (data) => api.post('/subscriptions', data),
  list: () => api.get('/subscriptions'),
  confirm: (id) => api.post(`/subscriptions/${id}/confirm`),
}

// Portfolio
export const portfolioAPI = {
  getSummary: () => api.get('/portfolio/summary'),
}

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  listUsers: (params) => api.get('/admin/users', { params }),
}

// Super Admin
export const superAdminAPI = {
  login: (data) => api.post('/super-admin/auth/login', data),
  getStats: (token) => api.get('/super-admin/stats', { headers: { Authorization: `Bearer ${token}` } }),
  listTenants: (token, params) => api.get('/super-admin/tenants', { headers: { Authorization: `Bearer ${token}` }, params }),
  createTenant: (token, data) => api.post('/super-admin/tenants', data, { headers: { Authorization: `Bearer ${token}` } }),
  getTenant: (token, id) => api.get(`/super-admin/tenants/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  updateTenant: (token, id, data) => api.put(`/super-admin/tenants/${id}`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateBranding: (token, id, data) => api.put(`/super-admin/tenants/${id}/branding`, data, { headers: { Authorization: `Bearer ${token}` } }),
  updateFeatures: (token, id, data) => api.put(`/super-admin/tenants/${id}/features`, data, { headers: { Authorization: `Bearer ${token}` } }),
  suspendTenant: (token, id) => api.post(`/super-admin/tenants/${id}/suspend`, {}, { headers: { Authorization: `Bearer ${token}` } }),
  activateTenant: (token, id) => api.post(`/super-admin/tenants/${id}/activate`, {}, { headers: { Authorization: `Bearer ${token}` } }),
}

export default api
